import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useRouterState } from '@tanstack/react-router'
import { Divider, Layout } from 'antd'
import clsx from 'clsx'
import { useSetAtom, useAtomValue } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useState, type FC } from 'react'
import toast from 'react-hot-toast'
import { FaUpload, FaHistory, FaThumbsUp } from 'react-icons/fa'

import { PopoverListGroupTitle, PopoverListItem } from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { bulkApproveRequests } from '@/features/vehicle-dispatch-boards/api'
import { vehicleDispatchBoardsQueryKeys } from '@/features/vehicle-dispatch-boards/query-keys'
import { type UserMeTenantExEntity, type UserMeExEntity } from '@/libs/api'
import { USER_TYPE } from '@/libs/api/types'
import { shippingDateAtom } from '@/routes/_protected/$tenantKey/-states/atoms/shipping-date.atom'

import { useTmsVisibility } from '../../-hooks'
import {
  headerPortalElementAtom,
  isOpenShipmentOrderCsvImportModalAtom,
  isOpenTourCsvImportModalAtom
} from '../../-states'
import { AppBreadcrumb } from '../AppBreadcrumb'
import { AuthUserPopoverTrigger } from '../AuthUserPopoverTrigger'
import { HeaderItem } from '../HeaderItem'

const { Header: AntdHeader } = Layout

type Props = {
  authUser: UserMeExEntity
  currentTenant: UserMeTenantExEntity
  onLogout: () => void
  tenantKey: string
}

export const Header: FC<Props> = ({
  authUser,
  currentTenant,
  onLogout,
  tenantKey
}) => {
  const setHeaderPortalElement = useSetAtom(headerPortalElementAtom)
  const [openImportJobsPopover, setOpenImportJobsPopover] = useState(false)
  const isUsingTms = useTmsVisibility(tenantKey, authUser)
  const queryClient = useQueryClient()
  const shippingDate = useAtomValue(shippingDateAtom) ?? ''
  const isVehicleDispatchBoard = useRouterState({
    select: (state) =>
      state.location.pathname.includes('/vehicle-dispatch-boards')
  })

  const handleImportJobsPopoverItem = useCallback(() => {
    setOpenImportJobsPopover(false)
  }, [])

  const handleOpenTourCsvImportModal = useAtomCallback(
    useCallback(
      (_, set) => {
        set(isOpenTourCsvImportModalAtom, true)
        handleImportJobsPopoverItem()
      },
      [handleImportJobsPopoverItem]
    )
  )

  const handleOpenShipmentOrderImportModal = useAtomCallback(
    useCallback(
      (_, set) => {
        set(isOpenShipmentOrderCsvImportModalAtom, true)
        handleImportJobsPopoverItem()
      },
      [handleImportJobsPopoverItem]
    )
  )

  const handleApproveRequests = useCallback(async () => {
    if (!isVehicleDispatchBoard) return
    try {
      const result = await bulkApproveRequests({
        tenantKey: currentTenant.key,
        shippingDate
      })

      if (result.failureCount > 0) {
        console.warn(
          ERROR_MESSAGES.FAILED_TO_SOME_BULK_APPROVE,
          result.results.filter((r) => !r.success)
        )
      }

      // 成功メッセージを表示
      toast.success(
        SUCCESS_MESSAGES.SUCCEEDED_TO_BULK_APPROVE(
          result.totalCount,
          result.successCount
        )
      )

      // データを再取得
      await queryClient.invalidateQueries({
        queryKey: vehicleDispatchBoardsQueryKeys.all
      })
    } catch (error) {
      console.error(ERROR_MESSAGES.BULK_APPROVE_ERROR, error)
      toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_BULK_APPROVE)
    }

    handleImportJobsPopoverItem()
  }, [
    isVehicleDispatchBoard,
    handleImportJobsPopoverItem,
    currentTenant.key,
    queryClient,
    shippingDate
  ])

  const sticky = useRouterState({
    select: (state) =>
      state.matches.some((match) => match.staticData.stickyHeader === true)
  })

  // 配車板ページの場合は、右側の固定メニュー(49px)に重ならないように
  // ヘッダー右側の操作アイコン群を 49px 左にオフセットする
  const isVehicleDispatchBoardsPage = useRouterState({
    select: (state) =>
      state.location.pathname.includes('/vehicle-dispatch-boards')
  })

  return (
    <AntdHeader
      className={clsx([
        'flex h-app-header items-center bg-white p-0',
        sticky && 'sticky top-0 z-[2]'
      ])}
    >
      <div className="flex w-full items-center gap-4 pl-6 pr-0">
        <AppBreadcrumb data-testid="breadcrumb" />
        <div className="flex-1" ref={(ref) => setHeaderPortalElement(ref)} />
        <div
          className={clsx([
            'sticky z-[3] ml-auto flex items-center gap-2 bg-white pr-8',
            isVehicleDispatchBoardsPage ? 'right-[49px]' : 'right-0'
          ])}
        >
          {authUser.type === USER_TYPE.OWNER && (
            <>
              <HeaderItem
                data-testid="import-jobs-popover"
                open={openImportJobsPopover}
                onOpenChange={setOpenImportJobsPopover}
                popoverContent={
                  <div className="-m-3">
                    <PopoverListGroupTitle className="!pt-3">
                      {LABELS.TOUR}
                    </PopoverListGroupTitle>
                    <PopoverListItem
                      icon={<FaUpload />}
                      onClick={handleOpenTourCsvImportModal}
                    >
                      {ACTIONS.IMPORT_FROM_CSV}
                    </PopoverListItem>

                    <Link
                      to="/$tenantKey/tour-import-jobs"
                      params={{
                        tenantKey: currentTenant.key
                      }}
                      onClick={handleImportJobsPopoverItem}
                    >
                      <PopoverListItem
                        icon={<FaHistory />}
                        className="[[data-status='active']_&>div]:!text-primary"
                      >
                        {LABELS.TOUR_IMPORT_HISTORY}
                      </PopoverListItem>
                    </Link>
                    {isUsingTms && (
                      <>
                        <PopoverListGroupTitle>
                          {LABELS.SHIPMENT_ORDER}
                        </PopoverListGroupTitle>
                        <PopoverListItem
                          icon={<FaUpload />}
                          onClick={handleOpenShipmentOrderImportModal}
                        >
                          {ACTIONS.IMPORT_SHIPMENT_ORDER}
                        </PopoverListItem>
                        <Link
                          to="/$tenantKey/shipment-order-import-jobs"
                          params={{
                            tenantKey: currentTenant.key
                          }}
                          onClick={handleImportJobsPopoverItem}
                        >
                          <PopoverListItem
                            icon={<FaHistory />}
                            className="[[data-status='active']_&>div]:!text-primary"
                          >
                            {LABELS.SHIPMENT_ORDER_IMPORT_HISTORY}
                          </PopoverListItem>
                        </Link>
                      </>
                    )}
                  </div>
                }
              >
                <FaUpload />
              </HeaderItem>
              <Divider type="vertical" className="m-0" />
            </>
          )}

          {/* パートナーユーザー専用の依頼承認ボタン */}
          {authUser.type === USER_TYPE.PARTNER && isVehicleDispatchBoard && (
            <>
              <HeaderItem
                data-testid="approve-requests-popover"
                popoverContent={
                  <div className="-m-3">
                    <PopoverListGroupTitle className="!pt-3">
                      {LABELS.DISPATCH}
                    </PopoverListGroupTitle>
                    <PopoverListItem
                      icon={<FaThumbsUp />}
                      onClick={handleApproveRequests}
                    >
                      {ACTIONS.APPROVE_REQUEST}
                    </PopoverListItem>
                  </div>
                }
              >
                <FaThumbsUp />
              </HeaderItem>
              <Divider type="vertical" className="m-0" />
            </>
          )}

          {/* MEMO: 通知機能は未実装 
          <HeaderItem dot={true}>
            <FaBell />
          </HeaderItem>        
          */}
          <AuthUserPopoverTrigger
            authUser={authUser}
            currentTenant={currentTenant}
            onLogout={onLogout}
          />
        </div>
      </div>
    </AntdHeader>
  )
}
